import React from "react";
import {
  Navigate,
  Route,
  Routes,
  unstable_HistoryRouter as HistoryRouter,
  useLocation,
  useNavigate,
  useParams,
} from "react-router-dom";

import { useMaintenanceStatus } from "api/queries/useMaintenance";
import { appHistory } from "./router/history";

import App from "./components/App";
import DashboardPage from "./components/DashboardPage";
import LoginPage from "./components/LoginPage";
import ChooseCustomerPage from "./components/Customer/ChooseCustomerPage";
import NotificationPage from "./components/Notification/NotificationPage";
import ProfilePage from "./components/Profile/ProfilePage";
import ProfileParametersPage from "./components/Profile/ProfileParametersPage";
import ProfileDelegatePage from "./components/Profile/Delegate/ProfileDelegatePage";
import ProfileDelegatePageList from "./components/Profile/Delegate/ProfileDelegatePageList";
import DocumentLibraryPage from "./components/DocumentLibrary/DocumentLibraryPage";
import DocumentLibraryDetailDocument from "./components/DocumentLibrary/DocumentLibraryDetailDocument";
import ContentPage from "./components/Common/ContentPage";
import NotFoundPage from "./components/Common/NotFoundPage";
import ResetPasswordPage from "./components/Password/Reset/ResetPasswordPage";
import BlockedAccountPage from "./components/Account/BlockedAccountPage";
import NewPasswordPage from "./components/Password/New/NewPasswordPage";
import EndUserAgreementPage from "./components/EndUserAgreementPage";
import BorrowCustomerPage from "./components/Customer/BorrowCustomerPage";
import { ROUTES } from "./config/routes";

import AuthenticatedComponent from "./AuthenticatedComponent";
import UnauthenticatedComponent from "./UnauthenticatedComponent";
import MaintenancePage from "./components/MaintenancePage";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const AsComp = (c: unknown) => c as React.ComponentType<any>;
const AppShell = App as unknown as React.ComponentType<{
  children: React.ReactElement;
  location: ReturnType<typeof useLocation>;
}>;
const Router = HistoryRouter as unknown as React.ComponentType<{
  history: typeof appHistory;
  children: React.ReactNode;
}>;

const DashboardRoute = AuthenticatedComponent(AsComp(DashboardPage), true);
const DocumentLibraryDetailRoute = AuthenticatedComponent(
  AsComp(DocumentLibraryDetailDocument),
  true
);
const DocumentLibraryRoute = AuthenticatedComponent(AsComp(DocumentLibraryPage), true);
const ProfileRoute = AuthenticatedComponent(AsComp(ProfilePage), true);
const ProfileParametersRoute = AuthenticatedComponent(AsComp(ProfileParametersPage), true);
const ProfileDelegateFormRoute = AuthenticatedComponent(AsComp(ProfileDelegatePage), true);
const ProfileDelegateListRoute = AuthenticatedComponent(AsComp(ProfileDelegatePageList), true);
const NotificationRoute = AuthenticatedComponent(AsComp(NotificationPage), true);
const ContentRoute = AuthenticatedComponent(AsComp(ContentPage), true);
const LoginRoute = UnauthenticatedComponent(AsComp(LoginPage));
const BlockedAccountRoute = UnauthenticatedComponent(AsComp(BlockedAccountPage));
const NewPasswordRoute = UnauthenticatedComponent(AsComp(NewPasswordPage));
const ResetPasswordRoute = UnauthenticatedComponent(AsComp(ResetPasswordPage));
const MaintenanceRoute = UnauthenticatedComponent(AsComp(MaintenancePage));

type RouteComponent = React.ComponentType<Record<string, unknown>>;

function RouteElement({ component: Component }: { component: RouteComponent }) {
  const params = useParams();
  const location = useLocation();

  return <Component params={params} location={location} />;
}

function AppRoute({ component }: { component: RouteComponent }) {
  const location = useLocation();

  return (
    <AppShell location={location}>
      <RouteElement component={component} />
    </AppShell>
  );
}

function MaintenanceGate({ children }: { children: React.ReactElement }) {
  const location = useLocation();
  const navigate = useNavigate();
  const maintenanceQuery = useMaintenanceStatus();

  React.useEffect(() => {
    if (!maintenanceQuery.isSuccess) {
      return;
    }

    if (
      maintenanceQuery.maintenance.isMaintenance &&
      location.pathname !== BASE_PATH + "/maintenance"
    ) {
      navigate(BASE_PATH + "/maintenance", { replace: true });
    }
  }, [
    location.pathname,
    maintenanceQuery.isSuccess,
    maintenanceQuery.maintenance.isMaintenance,
    navigate,
  ]);

  return children;
}

export default function AppRouter() {
  return (
    <Router history={appHistory}>
      <MaintenanceGate>
        <Routes>
          <Route path={ROUTES.dashboard.path} element={<AppRoute component={DashboardRoute} />} />
          <Route
            path={ROUTES.moduleLibraryDetail.path}
            element={<AppRoute component={DocumentLibraryDetailRoute} />}
          />
          <Route
            path={ROUTES.moduleLibrary.path}
            element={<AppRoute component={DocumentLibraryRoute} />}
          />
          <Route path={ROUTES.profile.path} element={<AppRoute component={ProfileRoute} />} />
          <Route
            path={ROUTES.profileParameters.path}
            element={<AppRoute component={ProfileParametersRoute} />}
          />
          <Route
            path={ROUTES.profileDelegateAdd.path}
            element={<AppRoute component={ProfileDelegateFormRoute} />}
          />
          <Route
            path={ROUTES.profileDelegateUpdate.path}
            element={<AppRoute component={ProfileDelegateFormRoute} />}
          />
          <Route
            path={ROUTES.profileDelegate.path}
            element={<AppRoute component={ProfileDelegateListRoute} />}
          />
          <Route
            path={ROUTES.blockedAccount.path}
            element={<AppRoute component={BlockedAccountRoute} />}
          />
          <Route
            path={ROUTES.newPassword.path}
            element={<AppRoute component={NewPasswordRoute} />}
          />
          <Route
            path={ROUTES.resetPassword.path}
            element={<AppRoute component={ResetPasswordRoute} />}
          />
          <Route
            path={ROUTES.notification.path}
            element={<AppRoute component={NotificationRoute} />}
          />
          <Route path={ROUTES.contentPage.path} element={<AppRoute component={ContentRoute} />} />
          <Route path={ROUTES.login.path} element={<AppRoute component={LoginRoute} />} />
          <Route
            path={ROUTES.chooseCustomer.path}
            element={<AppRoute component={AsComp(ChooseCustomerPage)} />}
          />
          <Route
            path={ROUTES.borrowCustomer.path}
            element={<AppRoute component={AsComp(BorrowCustomerPage)} />}
          />
          <Route
            path={ROUTES.borrowCustomerToken.path}
            element={<AppRoute component={AsComp(BorrowCustomerPage)} />}
          />
          <Route
            path={ROUTES.endUserAgreement.path}
            element={<AppRoute component={AsComp(EndUserAgreementPage)} />}
          />
          <Route
            path={ROUTES.maintenance.path}
            element={<AppRoute component={MaintenanceRoute} />}
          />
          <Route
            path={ROUTES.notFound.path}
            element={<AppRoute component={AsComp(NotFoundPage)} />}
          />
          <Route path="*" element={<Navigate to={BASE_PATH + "/"} replace />} />
        </Routes>
      </MaintenanceGate>
    </Router>
  );
}
